import { Injectable, signal } from '@angular/core';
import { EnumObject, EnumObjectExtended } from '@card-software/library';
import { forkJoin, Observable } from 'rxjs';
import { OrderEnumsProxyService } from '../proxies/order/order-enums.proxy.service';
import { ItemEnumsProxyService } from '../proxies/item/item-enums.proxy.service';
import { InventoryEnumsProxyService } from '../proxies/inventory/inventory-enums.proxy.service';
import { startCase } from 'lodash';
import _ from 'lodash';
import { FinanceEnumsProxyService } from '../proxies/finance/finance-enums.proxy.service';
import { LocaleHelperService } from './locale-helper.service';
import { ResponseTranslation } from '../models/Translations';

@Injectable({
  providedIn: 'root',
})
export class EnumService {
  private readonly itemEnumLocalStorageKey = 'itemEnums';
  private readonly orderEnumLocalStorageKey = 'orderEnums';
  private readonly inventoryEnumLocalStorageKey = 'inventoryEnums';
  private readonly financeEnumLocalStorageKey = 'financeEnums';
  public orderEnums = signal<EnumObject[]>([]);
  public itemEnums = signal<EnumObject[]>([]);
  public inventoryEnums = signal<EnumObject[]>([]);
  public financeEnums = signal<EnumObject[]>([]);

  constructor(
    private readonly orderEnumsProxyService: OrderEnumsProxyService,
    private readonly itemEnumsProxyService: ItemEnumsProxyService,
    private readonly inventoryEnumsProxyService: InventoryEnumsProxyService,
    private readonly financeEnumsProxyService: FinanceEnumsProxyService,
    private localHelperService: LocaleHelperService,
  ) {}

  public loadEnums(): Observable<boolean> {
    localStorage.removeItem(this.itemEnumLocalStorageKey);
    localStorage.removeItem(this.orderEnumLocalStorageKey);
    localStorage.removeItem(this.inventoryEnumLocalStorageKey);
    localStorage.removeItem(this.financeEnumLocalStorageKey);
    return new Observable<boolean>((observer) => {
      forkJoin([
        this.itemEnumsProxyService.getAll(),
        this.orderEnumsProxyService.getAll(),
        this.inventoryEnumsProxyService.getAll(),
        this.financeEnumsProxyService.getAll(),
      ]).subscribe({
        next: (response) => {
          localStorage.setItem(this.itemEnumLocalStorageKey, JSON.stringify(response[0]));
          this.itemEnums.set(response[0]);
          localStorage.setItem(this.orderEnumLocalStorageKey, JSON.stringify(response[1]));
          this.orderEnums.set(response[1]);
          localStorage.setItem(this.inventoryEnumLocalStorageKey, JSON.stringify(response[2]));
          this.inventoryEnums.set(response[2]);
          localStorage.setItem(this.financeEnumLocalStorageKey, JSON.stringify(response[3]));
          this.financeEnums.set(response[3]);

          observer.next(true);
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading enums', error);
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  public getEnum(enumName: string, enumType: 'itemEnums' | 'orderEnums' | 'inventoryEnums' | 'financeEnums'): EnumObjectExtended {
    let enumObj = null;
    enumObj = JSON.parse(localStorage.getItem(enumType)!);

    if (!enumObj || enumObj.length === 0) {
      this.loadEnums().subscribe({
        next: (response) => {
          if (response) {
            this.getEnum(enumName, enumType);
          }
        },
        error: (error) => {
          console.error('Error loading enums', error);
        },
      });
      return { name: enumName, values: {}, valuesEnglish: {} };
    }

    if (_.isNil(enumObj)) {
      return { name: enumName, values: {}, valuesEnglish: {} };
    }

    const matchedEnum = enumObj.find(
      (x: { name: string; values: Record<string, string | Record<string, string>>; valuesEnglish?: Record<string, string> }) =>
        x.name === enumName,
    );
    if (!matchedEnum) {
      return { name: enumName, values: {}, valuesEnglish: {} };
    }

    const formattedValues: Record<string, string> = {};
    for (const key in matchedEnum.values) {
      if (Object.prototype.hasOwnProperty.call(matchedEnum.values, key)) {
        const value = this.localHelperService.getLocalizedValue(matchedEnum.values[key] as ResponseTranslation);

        formattedValues[key] = enumName !== 'UnitOfMeasure' ? startCase(value) : value; // For UnitOfMeasure, we keep the original value
      }
    }

    const englishValues: Record<string, string> = {};
    if (matchedEnum.valuesEnglish) {
      for (const key in matchedEnum.valuesEnglish) {
        englishValues[key] = matchedEnum.valuesEnglish[key];
      }
    } else {
      for (const key in matchedEnum.values) {
        const value = matchedEnum.values[key];
        const valObj = value as Record<string, string>;
        englishValues[key] =
          typeof valObj === 'object' && valObj !== null && typeof valObj['en'] === 'string' ? valObj['en'] : String(value);
      }
    }

    return {
      name: matchedEnum.name,
      values: formattedValues,
      valuesEnglish: englishValues,
    };
  }
}
